import { createHashRouter, redirect, Outlet, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react';
import { useEffect } from 'react';
import { paths } from '@/navigation/paths';
import { authService } from '@/services/authService';
import { authStep } from '@/services/authStepService';
import Loading from '@/components/ui/Loading';

// Wrap dynamic imports so that if a code-split chunk fails to load (e.g., after a deploy),
// we trigger a hard reload to recover from a potentially stale runtime.
function lazyWithReload<T extends { default: ComponentType<unknown> }>(
  factory: () => Promise<T>
): LazyExoticComponent<T['default']> {
  return lazy(async () => {
    try {
      return await factory();
    } catch {
      // Force a reload to fetch fresh chunks
      window.location.reload();
      // Provide a valid module shape to satisfy types; this will be short-lived
      return { default: () => null };
    }
  });
}

const ErrorPage = lazyWithReload(() => import('@/pages/app/ErrorPage'));
const PhoneRequestPage = lazyWithReload(() => import('@/pages/auth/PhoneRequestPage'));
const ConfirmMethodPage = lazyWithReload(() => import('@/pages/auth/ConfirmMethodPage'));
const OneTimePasswordPage = lazyWithReload(() => import('@/pages/auth/OneTimePasswordPage'));
const RegistrationPage = lazyWithReload(() => import('@/pages/auth/RegistrationPage'));
const Dashboard = lazyWithReload(() => import('@/pages/app/Dashboard'));
const ProfilePage = lazyWithReload(() => import('@/pages/app/ProfilePage'));
const BarcodePage = lazyWithReload(() => import('@/pages/app/BarcodePage'));
const StorePage = lazyWithReload(() => import('@/pages/app/StorePage'));
const SupportPage = lazyWithReload(() => import('@/pages/app/SupportPage'));
const DiscountProductPage = lazyWithReload(() => import('@/pages/app/DiscountProductPage'));
const DiscountPage = lazyWithReload(() => import('@/pages/app/DiscountPage'));
const DiscountFiltersPage = lazyWithReload(() => import('@/pages/app/DiscountFiltersPage'));
const PromotionPage = lazyWithReload(() => import('@/pages/app/PromotionPage'));
const PromotionDetailPage = lazyWithReload(() => import('@/pages/app/PromotionDetailPage'));
const EventPage = lazyWithReload(() => import('@/pages/app/EventPage'));
const EventDetailPage = lazyWithReload(() => import('@/pages/app/EventDetailPage'));
const SurveyPage = lazyWithReload(() => import('@/pages/app/SurveyPage'));
const SurveyDetailPage = lazyWithReload(() => import('@/pages/app/SurveyDetailPage'));
const LoyaltyTermsPage = lazyWithReload(() => import('@/pages/app/LoyaltyTermsPage'));
const AccountPage = lazyWithReload(() => import('@/pages/app/AccountPage'));

const RECAPTCHA_VISIBLE_CLASS = 'show-recaptcha-badge';
const RECAPTCHA_VISIBLE_PATHS = new Set<string>([paths.auth.confirm, paths.auth.otp]);

function RecaptchaBadgeVisibilitySync(): null {
  const { pathname } = useLocation();

  useEffect(() => {
    const shouldShowRecaptchaBadge = RECAPTCHA_VISIBLE_PATHS.has(pathname);
    document.body.classList.toggle(RECAPTCHA_VISIBLE_CLASS, shouldShowRecaptchaBadge);

    return () => {
      document.body.classList.remove(RECAPTCHA_VISIBLE_CLASS);
    };
  }, [pathname]);

  return null;
}

async function rootRedirectLoader(): Promise<Response | null> {
  const { isAuthenticated } = await authService.checkAuth();
  if (isAuthenticated) return redirect(paths.app);

  // If not authenticated, derive the next step from local authStep state
  if (authStep.isRegisterReady()) return redirect(paths.auth.register);
  if (authStep.isOtpReady()) return redirect(paths.auth.otp);
  if (authStep.isPhoneConfirmReady()) return redirect(paths.auth.confirm);
  return redirect(paths.auth.phone);
}

async function ensureAuthenticatedLoader(): Promise<Response | null> {
  const { isAuthenticated } = await authService.checkAuth();
  if (isAuthenticated) return null;

  return redirect(paths.auth.phone);
}

async function ensureUnauthenticatedLoader(): Promise<Response | null> {
  const { isAuthenticated } = await authService.checkAuth();
  if (isAuthenticated) {
    return redirect(paths.app);
  }
  return null;
}

function ensureOtpStepLoader(): Response | null {
  if (!authStep.isOtpReady()) {
    return redirect(paths.auth.phone);
  }
  return null;
}

function ensureConfirmStepLoader(): Response | null {
  if (!authStep.isPhoneConfirmReady()) {
    return redirect(paths.auth.phone);
  }
  return null;
}

function ensureRegisterStepLoader(): Response | null {
  if (!authStep.isRegisterReady()) {
    return redirect(paths.auth.phone);
  }
  return null;
}

function AppLayout(): JSX.Element {
  return (
    <Suspense fallback={<Loading />}>
      <RecaptchaBadgeVisibilitySync />
      <Outlet />
    </Suspense>
  );
}

export const router = createHashRouter(
  [
    {
      path: paths.root,
      loader: rootRedirectLoader,
      element: <Navigate to={paths.auth.phone} />,
      errorElement: (
        <Suspense fallback={<Loading />}>
          <ErrorPage />
        </Suspense>
      ),
    },

    {
      element: <AppLayout />,
      errorElement: (
        <Suspense fallback={<Loading />}>
          <ErrorPage />
        </Suspense>
      ),
      children: [
        // Public auth routes
        {
          path: paths.auth.phone,
          element: <PhoneRequestPage />,
          loader: ensureUnauthenticatedLoader,
        },
        {
          path: paths.auth.confirm,
          element: <ConfirmMethodPage />,
          loader: ensureConfirmStepLoader,
        },
        { path: paths.auth.otp, element: <OneTimePasswordPage />, loader: ensureOtpStepLoader },
        {
          path: paths.auth.register,
          element: <RegistrationPage />,
          loader: ensureRegisterStepLoader,
        },
      ],
    },

    // Protected app routes via loader (pre-render redirect)
    {
      path: paths.app,
      element: <AppLayout />,
      errorElement: (
        <Suspense fallback={<Loading />}>
          <ErrorPage />
        </Suspense>
      ),
      loader: ensureAuthenticatedLoader,
      // Avoid re-running the auth check on every tab switch within /app.
      // We still run it on first entry or when navigating outside/into /app.
      shouldRevalidate: ({ currentUrl, nextUrl }) => {
        const inApp = (url: URL) => url.pathname.startsWith(paths.app);
        // If both current and next URLs are inside /app, skip revalidation.
        // This prevents an extra network roundtrip that delays tab changes.
        if (inApp(currentUrl) && inApp(nextUrl)) return false;
        return true;
      },
      children: [
        { index: true, element: <Dashboard /> },
        { path: 'profile', element: <ProfilePage /> },
        { path: 'account', element: <AccountPage /> },
        { path: 'barcode', element: <BarcodePage /> },
        { path: 'store', element: <StorePage /> },
        { path: 'support', element: <SupportPage /> },
        { path: 'loyalty-terms', element: <LoyaltyTermsPage /> },
        { path: 'discounts/:discountId', element: <DiscountPage /> },
        { path: 'discounts/:discountId/filters', element: <DiscountFiltersPage /> },
        { path: 'discounts/products/:id', element: <DiscountProductPage /> },
        { path: 'promotions', element: <PromotionPage /> },
        { path: 'promotions/:promotionId', element: <PromotionDetailPage /> },
        { path: 'events', element: <EventPage /> },
        { path: 'events/:eventId', element: <EventDetailPage /> },
        { path: 'surveys', element: <SurveyPage /> },
        { path: 'surveys/:surveyId', element: <SurveyDetailPage /> },
      ],
    },

    // Fallback
    { path: '*', element: <Navigate to={paths.root} /> },
  ],
  { basename: import.meta.env.BASE_URL }
);
